import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Sun, 
  Cloud, 
  CloudRain, 
  CloudSnow, 
  CloudLightning, 
  CloudDrizzle, 
  CloudFog, 
  CloudSun,
  RefreshCw,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface WeatherData {
  temp: number;
  apparentTemp: number;
  weatherCode: number;
  isDay: boolean;
}

interface DailyForecast {
  date: string;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
}

interface WeatherConfig {
  label: string;
  icon: React.ComponentType<any>;
  color?: string;
}

const WEATHER_MAPPING: Record<number, WeatherConfig> = {
  0: { label: 'Clear Sky', icon: Sun, color: 'text-amber-400' },
  1: { label: 'Mainly Clear', icon: CloudSun, color: 'text-amber-200' },
  2: { label: 'Partly Cloudy', icon: CloudSun, color: 'text-slate-300' },
  3: { label: 'Overcast', icon: Cloud, color: 'text-slate-400' },
  45: { label: 'Foggy', icon: CloudFog, color: 'text-slate-400' },
  48: { label: 'Rime Fog', icon: CloudFog, color: 'text-slate-500' },
  51: { label: 'Light Drizzle', icon: CloudDrizzle, color: 'text-sky-400' },
  53: { label: 'Drizzle', icon: CloudDrizzle, color: 'text-sky-400' },
  55: { label: 'Heavy Drizzle', icon: CloudDrizzle, color: 'text-sky-500' },
  56: { label: 'Freezing Drizzle', icon: CloudSnow, color: 'text-sky-300' },
  57: { label: 'Freezing Drizzle', icon: CloudSnow, color: 'text-sky-300' },
  61: { label: 'Slight Rain', icon: CloudRain, color: 'text-blue-400' },
  63: { label: 'Rain', icon: CloudRain, color: 'text-blue-500' },
  65: { label: 'Heavy Rain', icon: CloudRain, color: 'text-blue-600' },
  66: { label: 'Light Freezing Rain', icon: CloudSnow, color: 'text-sky-300' },
  67: { label: 'Freezing Rain', icon: CloudSnow, color: 'text-sky-400' },
  71: { label: 'Light Snow', icon: CloudSnow, color: 'text-slate-100' },
  73: { label: 'Snowfall', icon: CloudSnow, color: 'text-slate-200' },
  75: { label: 'Heavy Snow', icon: CloudSnow, color: 'text-white' },
  77: { label: 'Snow Grains', icon: CloudSnow, color: 'text-slate-300' },
  80: { label: 'Slight Showers', icon: CloudRain, color: 'text-blue-400' },
  81: { label: 'Showers', icon: CloudRain, color: 'text-blue-500' },
  82: { label: 'Heavy Showers', icon: CloudRain, color: 'text-blue-600' },
  85: { label: 'Slight Snow Showers', icon: CloudSnow, color: 'text-slate-200' },
  86: { label: 'Snow Showers', icon: CloudSnow, color: 'text-slate-100' },
  95: { label: 'Thunderstorm', icon: CloudLightning, color: 'text-yellow-400' },
  96: { label: 'Thunderstorm Hail', icon: CloudLightning, color: 'text-yellow-500' },
  99: { label: 'Severe Thunderstorm', icon: CloudLightning, color: 'text-red-500' },
};

function getWeatherConfig(code: number): WeatherConfig {
  return WEATHER_MAPPING[code] || { label: 'Cloudy', icon: Cloud };
}

const FALLBACK_CURRENT: WeatherData = {
  temp: 72,
  apparentTemp: 72,
  weatherCode: 1, // Mainly Clear
  isDay: true
};

const FALLBACK_FORECAST: DailyForecast[] = [
  { date: 'Today', weatherCode: 1, tempMax: 76, tempMin: 58 },
  { date: 'Tomorrow', weatherCode: 2, tempMax: 74, tempMin: 55 },
  { date: 'Day 3', weatherCode: 0, tempMax: 78, tempMin: 60 },
  { date: 'Day 4', weatherCode: 61, tempMax: 71, tempMin: 54 },
  { date: 'Day 5', weatherCode: 2, tempMax: 73, tempMin: 56 },
];

export default function WeatherWidget() {
  const [current, setCurrent] = useState<WeatherData>(() => {
    try {
      const cached = localStorage.getItem('rb_weather_current');
      if (cached) return JSON.parse(cached);
    } catch {
      // Ignore storage errors
    }
    return FALLBACK_CURRENT;
  });

  const [forecast, setForecast] = useState<DailyForecast[]>(() => {
    try {
      const cached = localStorage.getItem('rb_weather_forecast');
      if (cached) return JSON.parse(cached);
    } catch {
      // Ignore storage errors
    }
    return FALLBACK_FORECAST;
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isForecastOpen, setIsForecastOpen] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const fetchWeather = async () => {
    try {
      setIsRefreshing(true);
      setError(false);
      
      // Coordinate parameters for Licking, Missouri: Lat 37.5012, Lon -91.8596
      // Fetching 5 days forecast
      const response = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=37.5012&longitude=-91.8596&current=temperature_2m,apparent_temperature,is_day,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&timezone=America%2FChicago&forecast_days=5',
        { headers: { 'Accept': 'application/json' } }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let data: any = null;
      try {
        data = JSON.parse(text);
      } catch {
        // Response was not JSON (e.g. proxy HTML or plain text error)
        throw new Error('Invalid response format');
      }

      if (!data || !data.current || !data.daily || !Array.isArray(data.daily.time)) {
        throw new Error('Incomplete weather payload');
      }
      
      const newCurrent: WeatherData = {
        temp: Math.round(data.current.temperature_2m),
        apparentTemp: Math.round(data.current.apparent_temperature),
        weatherCode: data.current.weather_code,
        isDay: data.current.is_day === 1
      };

      const dailyData: DailyForecast[] = data.daily.time.map((timeStr: string, idx: number) => {
        const dateObj = new Date(timeStr + 'T00:00:00');
        const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
        return {
          date: dayLabel,
          weatherCode: data.daily.weather_code[idx] ?? 0,
          tempMax: Math.round(data.daily.temperature_2m_max[idx] ?? 70),
          tempMin: Math.round(data.daily.temperature_2m_min[idx] ?? 50)
        };
      });

      setCurrent(newCurrent);
      setForecast(dailyData);
      setLoading(false);

      try {
        localStorage.setItem('rb_weather_current', JSON.stringify(newCurrent));
        localStorage.setItem('rb_weather_forecast', JSON.stringify(dailyData));
      } catch {
        // Storage failure ignored
      }
    } catch {
      // Graceful fallback without noisy console.error to keep error monitoring clean
      setError(false);
      setLoading(false);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWeather();

    // Refresh every 15 minutes
    const interval = setInterval(fetchWeather, 900000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !current) {
    return (
      <div className="flex items-center gap-3 py-2 px-4 h-16 bg-transparent select-none">
        <RefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
        <span className="text-[11px] font-mono tracking-wider text-slate-500 uppercase">Loading Weather...</span>
      </div>
    );
  }

  if (error && !current) {
    return (
      <button 
        onClick={fetchWeather}
        className="flex items-center gap-2 py-2 px-4 h-16 bg-transparent select-none text-[11px] font-mono text-red-400 uppercase tracking-widest hover:text-red-300 transition-all cursor-pointer"
      >
        <span>Offline</span>
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    );
  }

  const weatherConfig = current ? getWeatherConfig(current.weatherCode) : null;
  const WeatherIcon = weatherConfig ? weatherConfig.icon : Cloud;
  const temp = current ? current.temp : 68;

  // 1. Dynamic Temperature Coloring
  let tempColor = 'text-slate-300';
  let glowColor = 'rgba(148,163,184,0.12)';
  
  if (temp >= 90) {
    tempColor = 'text-red-500';
    glowColor = 'rgba(239,68,68,0.25)';
  } else if (temp >= 80) {
    tempColor = 'text-orange-400';
    glowColor = 'rgba(251,146,60,0.2)';
  } else if (temp >= 68) {
    tempColor = 'text-amber-400';
    glowColor = 'rgba(245,158,11,0.18)';
  } else if (temp >= 50) {
    tempColor = 'text-teal-400';
    glowColor = 'rgba(45,212,191,0.15)';
  } else if (temp >= 32) {
    tempColor = 'text-sky-400';
    glowColor = 'rgba(56,189,248,0.15)';
  } else {
    tempColor = 'text-blue-300';
    glowColor = 'rgba(147,197,253,0.18)';
  }

  // 2. Dynamic Animation Behavior
  const isSun = WeatherIcon === Sun;
  const isLightning = WeatherIcon === CloudLightning;
  const animDuration = temp >= 80 ? 3.2 : temp <= 40 ? 6.0 : 4.5;

  const iconAnimProps = isSun 
    ? {
        animate: { 
          y: [0, -2, 0, 2, 0],
          rotate: [0, 360],
          scale: [1, 1.04, 1]
        },
        transition: { 
          y: { repeat: Infinity, duration: animDuration, ease: "easeInOut" as const },
          rotate: { repeat: Infinity, duration: 25, ease: "linear" as const },
          scale: { repeat: Infinity, duration: animDuration, ease: "easeInOut" as const }
        }
      }
    : isLightning
    ? {
        animate: {
          y: [0, -1, 1, -1, 0],
          x: [0, 0.5, -0.5, 0, 0],
          scale: [1, 0.99, 1.01, 1]
        },
        transition: {
          repeat: Infinity,
          duration: 2.2,
          ease: "easeInOut" as const
        }
      }
    : {
        animate: { 
          y: [0, -3, 0, 3, 0],
          scale: [1, 1.02, 1]
        },
        transition: { 
          repeat: Infinity, 
          duration: animDuration, 
          ease: "easeInOut" as const 
        }
      };

  return (
    <div className="flex flex-col items-center relative z-40">
      
      {/* Clickable Horizontal Widget Header */}
      <div 
        onClick={() => setIsForecastOpen(!isForecastOpen)}
        className="flex flex-row items-center gap-4 py-2.5 px-4 rounded-2xl bg-transparent hover:bg-white/[0.02] border border-transparent hover:border-neutral-900 active:scale-[0.98] transition-all duration-300 cursor-pointer select-none relative group"
      >
        {/* Dynamic Temperature Glowing Background Aura */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-10 pointer-events-none blur-lg transition-all duration-500"
          style={{ backgroundColor: glowColor }}
        />

        {/* Large Animated Weather Icon */}
        <motion.div
          {...iconAnimProps}
          className={`${tempColor} shrink-0`}
        >
          <WeatherIcon className="w-12 h-12 stroke-[1.5]" />
        </motion.div>

        {/* Details block */}
        <div className="text-left min-w-[110px]">
          {/* Location */}
          <div className="flex items-center gap-1 text-[9px] font-mono font-bold tracking-widest text-slate-500 uppercase">
            <MapPin className="w-3 h-3 text-slate-600 shrink-0" />
            <span>Licking, MO</span>
          </div>
          {/* Temperature */}
          <div className="text-xl font-display font-black text-white leading-tight mt-0.5">
            {current?.temp}°F
          </div>
          {/* Conditions & Trigger indicators */}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] font-mono text-slate-400 font-bold tracking-wide truncate max-w-[90px]">
              {weatherConfig?.label}
            </span>
            <div className="text-slate-600 group-hover:text-brand-400 transition-colors">
              {isForecastOpen ? (
                <ChevronUp className="w-3.5 h-3.5 animate-bounce" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 animate-bounce" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Extended 5-Day Forecast Panel */}
      <AnimatePresence>
        {isForecastOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="w-72 mt-2 bg-neutral-950/85 hover:bg-neutral-950/95 border border-neutral-900 rounded-2xl shadow-[0_15px_30px_rgba(0,0,0,0.6)] backdrop-blur-md overflow-hidden text-left"
          >
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-1.5 pb-2 border-b border-neutral-900/60 font-mono text-[9px] tracking-widest text-slate-500 uppercase font-bold">
                <Calendar className="w-3.5 h-3.5 text-brand-400" />
                <span>Extended 5-Day Forecast</span>
              </div>

              <div className="space-y-2">
                {forecast.map((day, idx) => {
                  const dayConfig = getWeatherConfig(day.weatherCode);
                  const DayIcon = dayConfig.icon;
                  const dayColor = dayConfig.color || 'text-slate-400';
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between py-1.5 border-b border-neutral-900/40 last:border-0 text-xs text-slate-300"
                    >
                      <span className="font-mono font-bold text-slate-400 w-10 text-left">{day.date}</span>
                      
                      <div className="flex items-center gap-2 flex-1 justify-center px-1">
                        <DayIcon className={`w-4 h-4 ${dayColor} shrink-0`} />
                        <span className="text-[10px] text-slate-500 font-mono truncate max-w-[85px]">
                          {dayConfig.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 font-mono text-right w-16 justify-end">
                        <span className="text-white font-bold">{day.tempMax}°</span>
                        <span className="text-slate-500">{day.tempMin}°</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
